import React from 'react';
import Table from '@amzn/meridian/table/table';
import TableCell from '@amzn/meridian/table/table-cell';
import TableRow from '@amzn/meridian/table/table-row';
import Loader from '@amzn/meridian/loader/loader';
import Alert from '@amzn/meridian/alert/alert';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortDirection } from '../../Types/SortDirection';
import EmptyState from '../../commons/EmptyState';
import {
  PastContactsTableEmptyStateDataTestIds,
  PastContactsTableSortColumn,
} from './PastContactsTypes';
import { PastContact } from '../../api/PastContacts/schema';
import playTokens from '@amzn/meridian-tokens/base/icon/play';
import bookmarkTokens from '@amzn/meridian-tokens/base/icon/bookmark';
import bookmarkFilledTokens from '@amzn/meridian-tokens/base/icon/bookmark-filled';
import {
  getOwnershipColour,
  formatDateTime,
  formatDuration,
} from './utils/PastContactsUtils';
import {
  CHANNEL_OPTIONS,
  OWNERSHIP_OPTIONS,
} from './utils/PastContactsConstants';
import Tag from '@amzn/meridian/tag/tag';
import Icon from '@amzn/meridian/icon/icon';
import Button from '@amzn/meridian/button/button';

interface PastContactsTableProps {
  pastContacts: PastContact[];
  loading: boolean;
  error?: boolean;
  sortColumn: PastContactsTableSortColumn;
  sortDirection: SortDirection;
  onSort: (
    column: PastContactsTableSortColumn,
    direction: SortDirection
  ) => void;
  onBookmarkClick: (pastContact: PastContact) => void;
  columnWidths?: {
    contactId: string;
    dateTime: string;
    channel: string;
    duration: string;
    sic: string;
    ownership: string;
    review: string;
    bookmarked: string;
  };
  showSicColumn?: boolean;
  onPlayClick: (pastContact: PastContact) => void;
}

const PastContactsTable: React.FC<PastContactsTableProps> = ({
  pastContacts,
  loading,
  error,
  sortColumn,
  sortDirection,
  onSort,
  onBookmarkClick,
  columnWidths,
  showSicColumn = true,
  onPlayClick,
}) => {
  const intl = useIntl();

  // Helper function to get translated channel label
  const getTranslatedChannel = (channel: string) => {
    const channelOption = CHANNEL_OPTIONS.find(
      option => option.value === channel
    );
    return channelOption
      ? intl.formatMessage({
          id: channelOption.translationId,
          defaultMessage: channelOption.defaultMessage,
        })
      : channel;
  };

  // Helper function to get translated ownership label
  const getTranslatedOwnership = (ownership: string) => {
    const ownershipOption = OWNERSHIP_OPTIONS.find(
      option => option.value === ownership
    );
    return ownershipOption
      ? intl.formatMessage({
          id: ownershipOption.translationId,
          defaultMessage: ownershipOption.defaultMessage,
        })
      : ownership;
  };

  // Default column widths if not provided
  const defaultWidths = {
    contactId: '200px',
    dateTime: '200px',
    channel: '100px',
    duration: '100px',
    sic: '250px',
    ownership: '160px',
    review: '100px',
    bookmarked: '100px',
  };

  const widths = columnWidths || defaultWidths;

  const handleSort = ({ sortColumn: column, sortDirection: direction }) => {
    onSort(column, direction);
  };

  if (error) {
    return (
      <Box height='400px' width='fill' spacingInset='300'>
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_training_experience_error_message'
            defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
          />
        </Alert>
      </Box>
    );
  }

  return (
    <Box width='100%' overflowY='auto' overflowX='hidden'>
      <Table
        headerRows={1}
        showDividers
        spacing='medium'
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        layout='fixed'
        onSort={handleSort}
      >
        <TableRow>
          <TableCell width={widths.contactId}>
            <FormattedMessage
              id='cs_perfportal_contact_id'
              defaultMessage='Contact ID'
            />
          </TableCell>
          <TableCell
            sortColumn={PastContactsTableSortColumn.TIME}
            width={widths.dateTime}
          >
            <FormattedMessage
              id='cs_perfportal_past_contacts_table_header_date_and_time'
              defaultMessage='Date and time'
            />
          </TableCell>
          <TableCell width={widths.channel}>
            <FormattedMessage
              id='cs_perfportal_channel'
              defaultMessage='Channel'
            />
          </TableCell>
          <TableCell width={widths.duration}>
            <FormattedMessage
              id='cs_perfportal_duration'
              defaultMessage='Duration'
            />
          </TableCell>
          {showSicColumn && (
            <TableCell width={widths.sic}>
              <FormattedMessage
                id='cs_perfportal_contact_recom_sic'
                defaultMessage='SIC'
                description='SIC code for recommended contact'
              />
            </TableCell>
          )}
          <TableCell
            sortColumn={PastContactsTableSortColumn.OWNERSHIP_TYPE}
            width={widths.ownership}
          >
            <FormattedMessage
              id='cs_perfportal_ownership'
              defaultMessage='Ownership'
            />
          </TableCell>
          <TableCell alignmentHorizontal='center' width={widths.review}>
            <FormattedMessage
              id='cs_perfportal_review'
              defaultMessage='Review'
            />
          </TableCell>
          <TableCell alignmentHorizontal='center' width={widths.bookmarked}>
            <FormattedMessage
              id='cs_perfportal_contact_table_tabname_bookmarked'
              defaultMessage='Bookmarked'
            />
          </TableCell>
        </TableRow>
        {loading ? (
          <TableRow>
            <TableCell columnSpan={showSicColumn ? 8 : 7}>
              <Row
                type='outline'
                width='100%'
                alignmentHorizontal='center'
                height='400px'
              >
                <Loader />
              </Row>
            </TableCell>
          </TableRow>
        ) : pastContacts && pastContacts.length > 0 ? (
          pastContacts.map(pastContact => (
            <TableRow key={pastContact.COMM_ID}>
              <TableCell width={widths.contactId}>
                <Text>{pastContact.COMM_ID}</Text>
              </TableCell>
              <TableCell width={widths.dateTime}>
                <Text>{formatDateTime(pastContact.TIME)}</Text>
              </TableCell>
              <TableCell width={widths.channel}>
                <Text>{getTranslatedChannel(pastContact.CHANNEL_NAME)}</Text>
              </TableCell>
              <TableCell width={widths.duration}>
                <Text>{formatDuration(pastContact.CALL_DURATION_SECONDS)}</Text>
              </TableCell>
              {showSicColumn && (
                <TableCell width={widths.sic}>
                  <Text>{pastContact.SIC_NODE_1_NAME}</Text>
                </TableCell>
              )}
              <TableCell width={widths.ownership}>
                <Tag type={getOwnershipColour(pastContact.OWNERSHIP_TYPE)}>
                  {getTranslatedOwnership(pastContact.OWNERSHIP_TYPE)}
                </Tag>
              </TableCell>
              <TableCell alignmentHorizontal='center' width={widths.review}>
                <Button type='icon' onClick={() => onPlayClick(pastContact)}>
                  <Icon tokens={playTokens} color='primary' />
                </Button>
              </TableCell>
              <TableCell alignmentHorizontal='center' width={widths.bookmarked}>
                <Button
                  type='icon'
                  onClick={() => onBookmarkClick(pastContact)}
                >
                  <Icon
                    tokens={
                      pastContact.SAVED ? bookmarkFilledTokens : bookmarkTokens
                    }
                    color='primary'
                  />
                </Button>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell columnSpan={showSicColumn ? 8 : 7}>
              <EmptyState
                rowWidths={['grid-12']}
                innerColumnSpacing='600 none 600 none'
                data-testid={PastContactsTableEmptyStateDataTestIds}
              >
                <>
                  <FormattedMessage
                    id='cs_perfportal_havent_handled_contacts_week'
                    defaultMessage='There are no contacts to review for the selected week.'
                  />
                  <br></br>
                  <FormattedMessage
                    id='cs_perfportal_diff_filters'
                    defaultMessage='Reset the filters to try again.'
                  />
                </>
              </EmptyState>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </Box>
  );
};

export default PastContactsTable;
